import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';

const ORDERS_STALE_TIME_MS = 30_000;

type PurchaseOrdersQuery = RouterOutputs['purchaseOrders']['getOrders'];
type GetOrdersInput = RouterInputs['purchaseOrders']['getOrders'];

type UseOrderQueriesOptions = {
  page?: number;
  pageSize?: number;
  orderType?: GetOrdersInput['orderType'];
  status?: GetOrdersInput['status'];
  statuses?: GetOrdersInput['statuses'];
  supplierId?: GetOrdersInput['supplierId'];
  /** BLU-1268: standard vs just-in-time; filtered in the query, before the page is cut. */
  kind?: GetOrdersInput['kind'];
  searchTerm?: string;
  sortBy?: GetOrdersInput['sortBy'];
  sortOrder?: GetOrdersInput['sortOrder'];
  /** False makes no call (the PO list's To buy tab shows other data). Default true. */
  enabled?: boolean;
};

/**
 * Hook for purchase order read operations (queries)
 * Handles fetching orders with caching and automatic refetching.
 * When no location is selected ("All Locations"), fetches all orders for the org.
 */
export function useOrderQueries(options?: UseOrderQueriesOptions) {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();

  const page = Math.max(1, options?.page ?? 1);
  const pageSize = Math.max(1, options?.pageSize ?? 50);

  const input: GetOrdersInput = {
    ...(selectedLocation?.id ? { locationId: selectedLocation.id } : {}),
    ...(options?.orderType ? { orderType: options.orderType } : {}),
    ...(options?.status ? { status: options.status } : {}),
    ...(options?.statuses?.length ? { statuses: options.statuses } : {}),
    ...(options?.supplierId ? { supplierId: options.supplierId } : {}),
    ...(options?.kind ? { kind: options.kind } : {}),
    ...(options?.searchTerm?.trim() ? { searchTerm: options.searchTerm.trim() } : {}),
    ...(options?.sortBy ? { sortBy: options.sortBy } : {}),
    ...(options?.sortOrder ? { sortOrder: options.sortOrder } : {}),
    ...(options ? { limit: pageSize, offset: (page - 1) * pageSize } : {}),
  };

  const {
    data,
    isLoading: loading,
    isFetching,
    error,
    refetch
  } = trpc.purchaseOrders.getOrders.useQuery(
    input,
    {
      enabled: !!organization && (options?.enabled ?? true),
      staleTime: ORDERS_STALE_TIME_MS,
      retry: 2,
      placeholderData: (previous) => previous,
    }
  );

  const orders = data?.data ?? ([] as PurchaseOrdersQuery['data']);
  const pagination = data?.pagination ?? {
    total: orders.length,
    limit: orders.length || 1,
    offset: 0,
    totalPages: 1,
  };

  return {
    orders,
    pagination,
    loading: loading || !organization,
    isFetching,
    error,
    refreshOrders: refetch,
    selectedLocation
  };
}
